// src/components/kit/link-in-bio/LinkInBioAnalytics.tsx

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLinkInBioAnalytics, useExportSignups, useDownloadEmailList, useLinkInBioPage, useCreateLinkInBioTrackableUrl, useDeleteLinkInBioTrackableUrl } from '@/hooks/useLinkInBio';
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';
import Spinner from '@/components/common/Spinner';

type RangeKey = '7d' | '30d';

export default function LinkInBioAnalytics() {
  const navigate = useNavigate();
  const [range, setRange] = useState<RangeKey>('30d');
  const { data, isLoading } = useLinkInBioAnalytics(range);
  const { data: pageData } = useLinkInBioPage();
  const exportMutation = useExportSignups();
  const downloadEmailsMutation = useDownloadEmailList();
  const createTrackableUrl = useCreateLinkInBioTrackableUrl();
  const deleteTrackableUrl = useDeleteLinkInBioTrackableUrl();
  const { copy } = useCopyToClipboard();
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [newUrlName, setNewUrlName] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-neutral-500">No analytics data available.</p>
      </div>
    );
  }

  const { summary, views_by_day, clicks_by_link, traffic_sources, views_by_trackable_url } = data;
  const trackableUrls = pageData?.page?.trackable_urls ?? [];

  // Build a map of tracking_id -> views from analytics data
  const viewsByTrackingId: Record<string, number> = {};
  for (const entry of views_by_trackable_url) {
    viewsByTrackingId[entry.tracking_id] = entry.views;
  }

  const handleCreateTrackableUrl = async () => {
    const trimmed = newUrlName.trim();
    if (!trimmed) return;
    await createTrackableUrl.mutateAsync(trimmed);
    setNewUrlName('');
    setShowCreateForm(false);
  };

  const handleCopy = (trackingId: string, url: string) => {
    copy(url);
    setCopiedId(trackingId);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleDelete = async (trackingId: string) => {
    await deleteTrackableUrl.mutateAsync(trackingId);
    setDeleteConfirmId(null);
  };

  return (
    <div className="mx-auto max-w-3xl p-4 sm:p-6">
      {/* Header */}
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/app/kit/link-in-bio')}
              className="flex items-center gap-1 text-sm text-neutral-500 hover:text-neutral-700"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back
            </button>
            <h1 className="text-xl font-bold text-neutral-800">Link-in-Bio Analytics</h1>
          </div>
        </div>

        {/* Range selector */}
        <div className="flex gap-1 self-start rounded-lg bg-neutral-300 p-1 sm:self-auto">
          {(['7d', '30d'] as RangeKey[]).map((r) => (
            <button
              key={r}
              onClick={() => setRange(r)}
              className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                range === r
                  ? 'bg-neutral-200 text-neutral-800 shadow-sm'
                  : 'text-neutral-500 hover:text-neutral-700'
              }`}
            >
              {r}
            </button>
          ))}
        </div>
      </div>

      {/* Summary cards */}
      <div className="mb-8 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-5">
        <div className="rounded-xl border border-neutral-300 bg-neutral-200 p-3 sm:p-4">
          <p className="text-xs text-neutral-500">Page Views</p>
          <p className="mt-1 text-2xl font-bold text-neutral-800">{summary.total_views}</p>
        </div>
        <div className="rounded-xl border border-neutral-300 bg-neutral-200 p-3 sm:p-4">
          <p className="text-xs text-neutral-500">Unique Visitors</p>
          <p className="mt-1 text-2xl font-bold text-neutral-800">{summary.unique_visitors}</p>
        </div>
        <div className="rounded-xl border border-neutral-300 bg-neutral-200 p-3 sm:p-4">
          <p className="text-xs text-neutral-500">Link Clicks</p>
          <p className="mt-1 text-2xl font-bold text-neutral-800">{summary.total_clicks}</p>
        </div>
        <div className="rounded-xl border border-neutral-300 bg-neutral-200 p-3 sm:p-4">
          <p className="text-xs text-neutral-500">Click Rate</p>
          <p className="mt-1 text-2xl font-bold text-neutral-800">
            {(summary.click_through_rate * 100).toFixed(1)}%
          </p>
        </div>
        <div className="rounded-xl border border-neutral-300 bg-neutral-200 p-3 sm:p-4">
          <p className="text-xs text-neutral-500">Email Signups</p>
          <p className="mt-1 text-2xl font-bold text-neutral-800">{summary.total_signups}</p>
        </div>
      </div>

      {/* Views by day */}
      {views_by_day.length > 0 && (
        <div className="mb-8 rounded-xl border border-neutral-300 bg-neutral-200 p-4">
          <h3 className="mb-4 text-sm font-semibold text-neutral-700">Views by Day</h3>
          <div className="space-y-2">
            {views_by_day.slice(0, 14).map((day) => {
              const maxViews = Math.max(...views_by_day.map((d) => d.views), 1);
              const width = (day.views / maxViews) * 100;
              return (
                <div key={day.date} className="flex items-center gap-2 sm:gap-3">
                  <span className="w-20 flex-shrink-0 text-xs text-neutral-500 sm:w-24">{day.date}</span>
                  <div className="min-w-0 flex-1">
                    <div
                      className="h-5 rounded bg-primary-600"
                      style={{ width: `${Math.max(width, 2)}%` }}
                    />
                  </div>
                  <span className="w-8 flex-shrink-0 text-right text-xs font-medium text-neutral-700 sm:w-10">
                    {day.views}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Clicks by link */}
      {clicks_by_link.length > 0 && (
        <div className="mb-8 rounded-xl border border-neutral-300 bg-neutral-200 p-4">
          <h3 className="mb-4 text-sm font-semibold text-neutral-700">Clicks by Link</h3>
          <div className="space-y-3">
            {clicks_by_link.map((link) => (
              <div key={link.link_id} className="flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-neutral-700">{link.label}</p>
                  <div className="mt-1 h-2 w-full rounded-full bg-neutral-300">
                    <div
                      className="h-2 rounded-full bg-primary-600"
                      style={{ width: `${link.percentage * 100}%` }}
                    />
                  </div>
                </div>
                <div className="flex-shrink-0 text-right">
                  <p className="text-sm font-bold text-neutral-800">{link.clicks}</p>
                  <p className="text-xs text-neutral-500">{(link.percentage * 100).toFixed(0)}%</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Traffic sources */}
      {traffic_sources.length > 0 && (
        <div className="mb-8 rounded-xl border border-neutral-300 bg-neutral-200 p-4">
          <h3 className="mb-4 text-sm font-semibold text-neutral-700">Traffic Sources</h3>
          <div className="space-y-2">
            {traffic_sources.map((source) => (
              <div key={source.source} className="flex items-center justify-between gap-2">
                <span className="min-w-0 truncate text-sm text-neutral-700">{source.source}</span>
                <div className="flex flex-shrink-0 items-center gap-2">
                  <span className="text-sm font-medium text-neutral-800">{source.views}</span>
                  <span className="text-xs text-neutral-500">
                    ({(source.percentage * 100).toFixed(0)}%)
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Trackable URLs */}
      <div className="mb-8 rounded-xl border border-neutral-300 bg-neutral-200 p-4">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-neutral-700">Trackable URLs</h3>
          {!showCreateForm && (
            <button
              onClick={() => setShowCreateForm(true)}
              className="rounded-lg bg-primary-600 px-3 py-1.5 text-xs font-medium text-black transition-colors hover:bg-primary-700"
            >
              Create Trackable URL
            </button>
          )}
        </div>

        {/* Create form */}
        {showCreateForm && (
          <div className="mb-4 flex gap-2">
            <input
              type="text"
              value={newUrlName}
              onChange={(e) => setNewUrlName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreateTrackableUrl()}
              placeholder="e.g., Instagram Bio"
              maxLength={100}
              className="min-w-0 flex-1 rounded-lg border border-neutral-300 bg-neutral-100 px-3 py-1.5 text-sm text-neutral-800 placeholder-neutral-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              autoFocus
            />
            <button
              onClick={handleCreateTrackableUrl}
              disabled={!newUrlName.trim() || createTrackableUrl.isPending}
              className="rounded-lg bg-primary-600 px-3 py-1.5 text-xs font-medium text-black transition-colors hover:bg-primary-700 disabled:opacity-50"
            >
              {createTrackableUrl.isPending ? 'Creating...' : 'Create'}
            </button>
            <button
              onClick={() => { setShowCreateForm(false); setNewUrlName(''); }}
              className="rounded-lg border border-neutral-300 px-3 py-1.5 text-xs font-medium text-neutral-600 transition-colors hover:bg-neutral-300"
            >
              Cancel
            </button>
          </div>
        )}

        {/* List of trackable URLs */}
        {trackableUrls.length > 0 ? (
          <div className="space-y-3">
            {trackableUrls.map((t) => (
              <div key={t.tracking_id} className="rounded-lg border border-neutral-300 bg-neutral-100 p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-neutral-800">{t.name}</p>
                      <span className="flex-shrink-0 rounded-full bg-neutral-300 px-2 py-0.5 text-xs font-medium text-neutral-900">
                        {viewsByTrackingId[t.tracking_id] ?? 0} views
                      </span>
                    </div>
                    <p className="mt-1 truncate text-xs text-neutral-500">{t.public_url}</p>
                  </div>
                  <div className="flex flex-shrink-0 items-center gap-1">
                    <button
                      onClick={() => handleCopy(t.tracking_id, t.public_url)}
                      className="rounded-md p-1.5 text-neutral-500 transition-colors hover:bg-neutral-300 hover:text-neutral-700"
                      title="Copy URL"
                    >
                      {copiedId === t.tracking_id ? (
                        <svg className="h-4 w-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                      ) : (
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                      )}
                    </button>
                    {deleteConfirmId === t.tracking_id ? (
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleDelete(t.tracking_id)}
                          disabled={deleteTrackableUrl.isPending}
                          className="rounded-md px-2 py-1 text-xs font-medium text-red-600 transition-colors hover:bg-red-50"
                        >
                          Confirm
                        </button>
                        <button
                          onClick={() => setDeleteConfirmId(null)}
                          className="rounded-md px-2 py-1 text-xs font-medium text-neutral-500 transition-colors hover:bg-neutral-300"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setDeleteConfirmId(t.tracking_id)}
                        className="rounded-md p-1.5 text-neutral-400 transition-colors hover:bg-neutral-300 hover:text-red-600"
                        title="Delete"
                      >
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          !showCreateForm && (
            <p className="text-center text-sm text-neutral-500">
              No trackable URLs yet. Create one to track views from specific sources.
            </p>
          )
        )}
      </div>

      {/* Email signups — compact view with count and export buttons */}
      <div className="rounded-xl border border-neutral-300 bg-neutral-200 p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-sm font-semibold text-neutral-700">Email Signups</h3>
            <p className="mt-1 text-2xl font-bold text-neutral-800">{summary.total_signups}</p>
            <p className="text-xs text-neutral-500">
              {summary.total_signups === 1 ? 'subscriber' : 'subscribers'} collected
            </p>
          </div>
          {summary.total_signups > 0 && (
            <div className="flex flex-col gap-2 sm:flex-row">
              <button
                onClick={() => exportMutation.mutate()}
                disabled={exportMutation.isPending}
                className="rounded-lg border border-neutral-300 px-3 py-1.5 text-xs font-medium text-neutral-600 transition-colors hover:bg-neutral-300"
              >
                {exportMutation.isPending ? 'Exporting...' : 'Export CSV'}
              </button>
              <button
                onClick={() => downloadEmailsMutation.mutate()}
                disabled={downloadEmailsMutation.isPending}
                className="rounded-lg border border-neutral-300 px-3 py-1.5 text-xs font-medium text-neutral-600 transition-colors hover:bg-neutral-300"
              >
                {downloadEmailsMutation.isPending ? 'Downloading...' : 'Download Email List'}
              </button>
            </div>
          )}
        </div>
        {summary.total_signups === 0 && (
          <p className="mt-3 text-center text-sm text-neutral-500">No signups yet.</p>
        )}
      </div>
    </div>
  );
}
